import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { getUserIdFromToken } from "./utils/auth";

const WebSocketContext = createContext(null);
export const useWebSocket = () => useContext(WebSocketContext);

export const WebSocketProvider = ({ children }) => {
  const wsRef = useRef(null);
  const subscribersRef = useRef([]);
  const reconnectTimeoutRef = useRef(null);

  const [senderId, setSenderId] = useState(null);
  const [wsReady, setWsReady] = useState(false);

  // ✅ Browser-safe WebSocket connect (NO Bearer)
  const connectWebSocket = useCallback((userId) => {
    if (!userId) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const token = localStorage.getItem("authToken");
    if (!token) return console.warn("No auth token found");

    const wsUrl = `wss://yz2w3uyssa.execute-api.ap-south-1.amazonaws.com/production?userId=${userId}&Authorization=${token}`;
    const ws = new WebSocket(wsUrl);

    wsRef.current = ws;

    ws.onopen = () => {
      console.log("🟢 WS CONNECTED");
      setWsReady(true);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        subscribersRef.current.forEach((cb) => cb(data));
      } catch (err) {
        console.error("WS parse error", err);
      }
    };

    ws.onerror = (err) => {
      console.error("WS ERROR", err);
    };

    ws.onclose = (event) => {
      console.log(
        "🔴 WS CLOSED — retrying...",
        `Code: ${event.code}, Reason: ${event.reason}, WasClean: ${event.wasClean}`
      );
      setWsReady(false);
      reconnectTimeoutRef.current = setTimeout(
        () => connectWebSocket(userId),
        3000
      );
    };
  }, []);

  // Init WebSocket
  useEffect(() => {
    const init = async () => {
      const uid = await getUserIdFromToken();
      if (!uid) return;

      setSenderId(uid);
      connectWebSocket(uid);
    };

    init();

    return () => {
      wsRef.current?.close();
      clearTimeout(reconnectTimeoutRef.current);
    };
  }, [connectWebSocket]);

  const sendMessage = (msg) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    } else {
      console.warn("WS not ready");
    }
  };

  const subscribe = (callback) => {
    subscribersRef.current.push(callback);
    return () => {
      subscribersRef.current = subscribersRef.current.filter(
        (cb) => cb !== callback
      );
    };
  };

  return (
    <WebSocketContext.Provider
      value={{ sendMessage, subscribe, wsReady, senderId }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};
